import { c as _c } from "react/compiler-runtime";
import React from "react";
import { CONFIGS } from "@/constants/configs";
import { formatCalendarDate } from "@/lib/calendars";
import { useLocalization } from "@/providers/useLocalization";
import { useTranslations } from "@/providers/TranslationsProvider";
import type { ApplicationStatus } from "@/lib/userApplication";
import Button from "@/components/Button";
const TONES = {
  pending_review: {
    color: "#FF9F0A",
    icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
  },
  approved: {
    color: "#34C759",
    icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
  },
  rejected: {
    color: "#FF3B30",
    icon: "M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
  }
} as const;
interface StatusCardProps {
  status: Extract<ApplicationStatus, keyof typeof TONES>;
  rejectionReason?: string | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  onEdit?: () => void;
}
type StepState = "done" | "current" | "todo" | "failed";

/** Submitted → under review → decision, so applicants can follow where it stands. */
function Tracker(t0) {
  const $ = _c(42);
  const {
    status,
    submittedAt,
    reviewedAt
  } = t0;
  const {
    t
  } = useTranslations();
  const {
    config,
    language
  } = useLocalization();
  let t1;
  if ($[0] !== config || $[1] !== language) {
    t1 = value => value ? formatCalendarDate(value, config.calendar, config.dateFormat, language) : undefined;
    $[0] = config;
    $[1] = language;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const date = t1;
  const decided = status !== "pending_review";
  let t2;
  if ($[3] !== t) {
    t2 = t("join.track.decision", "Decision");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let decision = t2;
  let decisionState = "todo";
  if (status === "approved") {
    let t3;
    if ($[5] !== t) {
      t3 = t("join.track.approved", "Approved");
      $[5] = t;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    decision = t3;
    decisionState = "done";
  } else {
    if (status === "rejected") {
      let t3;
      if ($[7] !== t) {
        t3 = t("join.track.rejected", "Not approved");
        $[7] = t;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      decision = t3;
      decisionState = "failed";
    }
  }
  let t3;
  if ($[9] !== t) {
    t3 = t("join.track.submitted", "Submitted");
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== date || $[12] !== submittedAt) {
    t4 = date(submittedAt);
    $[11] = date;
    $[12] = submittedAt;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = {
      label: t3,
      state: "done",
      date: t4
    };
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t) {
    t6 = t("join.track.review", "Under review");
    $[17] = t;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const t7 = decided ? "done" : "current";
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = {
      label: t6,
      state: t7
    };
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = decision;
  const t10 = decisionState;
  let t11;
  if ($[22] !== date || $[23] !== reviewedAt) {
    t11 = date(reviewedAt);
    $[22] = date;
    $[23] = reviewedAt;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== decision || $[26] !== decisionState || $[27] !== t11) {
    t12 = {
      label: t9,
      state: t10,
      date: t11
    };
    $[25] = decision;
    $[26] = decisionState;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t5 || $[31] !== t8) {
    t13 = [t5, t8, t12];
    $[29] = t12;
    $[30] = t5;
    $[31] = t8;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const steps = t13;
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      done: "bg-[#34C759] text-white",
      current: "bg-[#FF9F0A] text-white ring-4 ring-[#FF9F0A]/20",
      failed: "bg-[#FF3B30] text-white",
      todo: "bg-[#E8E8ED] dark:bg-[#3A3A3C] text-[#86868B]"
    };
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const dot = t14;
  let t15;
  if ($[34] !== t) {
    t15 = t("join.track.title", "Application progress");
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== language || $[37] !== steps) {
    t16 = steps.map((step, i) => <li key={step.label} className="relative flex flex-col items-center" aria-current={step.state === "current" ? "step" : undefined}>{i > 0 && <span aria-hidden={true} className={`absolute top-3 end-1/2 w-full h-0.5 -translate-y-1/2 ${step.state === "todo" ? "bg-[#E8E8ED] dark:bg-[#3A3A3C]" : "bg-[#34C759]"}`} />}<span className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-semibold ${dot[step.state]}`}>{step.state === "done" && "\u2713"}{step.state === "failed" && "!"}{(step.state === "current" || step.state === "todo") && (i + 1).toLocaleString(language)}</span><span className={`mt-2 text-[12px] sm:text-[13px] leading-tight ${step.state === "todo" ? "text-[#86868B]" : "text-[var(--foreground)] font-medium"}`}>{step.label}</span>{step.date && <span dir="ltr" className="mt-0.5 text-[11px] sm:text-[12px] text-[#86868B] tabular-nums">{step.date}</span>}</li>);
    $[36] = language;
    $[37] = steps;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t15 || $[40] !== t16) {
    t17 = <ol className="mt-6 sm:mt-8 mx-auto max-w-md grid grid-cols-3 text-center" aria-label={t15}>{t16}</ol>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
}

/** What a doctor sees after submitting: under review, approved, or rejected with the reason. */
export default function StatusCard(t0) {
  const $ = _c(66);
  const {
    status,
    rejectionReason,
    submittedAt,
    reviewedAt,
    onEdit
  } = t0;
  const {
    t
  } = useTranslations();
  const tone = TONES[status];
  let t1;
  if ($[0] !== t) {
    t1 = t("join.pending title", "Your application is under review");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("join.pending body", "We're verifying your licence. We'll let you know as soon as you're approved - usually within two working days.");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      title: t1,
      body: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("join.approved title", "You're a {{app}} doctor");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("join.approved body", "Complete your public profile in your dashboard - it's what patients see - then set your availability and session prices.");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = {
      title: t4,
      body: t5
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("join.rejected title", "We couldn't approve your application");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("join.rejected body", "Please update your details and submit again.");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = {
      title: t7,
      body: t8
    };
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t3 || $[22] !== t6 || $[23] !== t9) {
    t10 = {
      pending_review: t3,
      approved: t6,
      rejected: t9
    };
    $[21] = t3;
    $[22] = t6;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const copy = t10[status];
  const t11 = `${tone.color}1f`;
  let t12;
  if ($[25] !== t11 || $[26] !== tone.color) {
    t12 = {
      backgroundColor: t11,
      color: tone.color
    };
    $[25] = t11;
    $[26] = tone.color;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== tone.icon) {
    t13 = <svg className="h-7 w-7 sm:h-8 sm:w-8" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden={true}><path strokeLinecap="round" strokeLinejoin="round" d={tone.icon} /></svg>;
    $[28] = tone.icon;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t12 || $[31] !== t13) {
    t14 = <div className="mx-auto mb-5 sm:mb-6 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl" style={t12}>{t13}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== copy.title) {
    t15 = <h2 className="text-[19px] sm:text-[22px] font-bold tracking-tight text-[var(--foreground)]">{copy.title}</h2>;
    $[33] = copy.title;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== copy.body) {
    t16 = <p className="mt-2 text-[14px] sm:text-[15px] leading-[1.5] text-[var(--foreground-secondary)] max-w-md mx-auto text-justify [text-align-last:center] hyphens-auto">{copy.body}</p>;
    $[35] = copy.body;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== reviewedAt || $[38] !== status || $[39] !== submittedAt) {
    t17 = <Tracker status={status} submittedAt={submittedAt} reviewedAt={reviewedAt} />;
    $[37] = reviewedAt;
    $[38] = status;
    $[39] = submittedAt;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== rejectionReason || $[42] !== status || $[43] !== t) {
    t18 = status === "rejected" && rejectionReason && <blockquote className="mt-5 mx-auto max-w-md rounded-xl bg-[#FF3B30]/[0.06] px-4 py-3 text-justify hyphens-auto text-[13px] sm:text-[14px] text-[var(--foreground)]"><span className="block mb-1 text-[12px] font-semibold text-[#FF3B30]">{t("join.reviewer note", "Note from our team")}</span><span dir="auto" className="block">{rejectionReason}</span></blockquote>;
    $[41] = rejectionReason;
    $[42] = status;
    $[43] = t;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== status || $[46] !== t) {
    t19 = status === "approved" && <a href={`${CONFIGS.routes.dashboard}/settings#profile`}><Button variant="apple" size="xl">{t("join.complete profile", "Complete your public profile")}</Button></a>;
    $[45] = status;
    $[46] = t;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== status || $[49] !== t) {
    t20 = status === "pending_review" && <a href={`${CONFIGS.routes.dashboard}/settings#profile`}><Button variant="apple-gray" size="xl">{t("join.follow in dashboard", "Follow it in your dashboard")}</Button></a>;
    $[48] = status;
    $[49] = t;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== onEdit || $[52] !== status || $[53] !== t) {
    t21 = status === "rejected" && onEdit && <Button variant="apple" size="xl" onClick={onEdit}>{t("join.update application", "Update application")}</Button>;
    $[51] = onEdit;
    $[52] = status;
    $[53] = t;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t19 || $[56] !== t20 || $[57] !== t21) {
    t22 = <div className="mt-6 sm:mt-8">{t19}{t20}{t21}</div>;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== t14 || $[60] !== t15 || $[61] !== t16 || $[62] !== t17 || $[63] !== t18 || $[64] !== t22) {
    t23 = <div className="rounded-[2rem] bg-white dark:bg-[#1C1C1E] ring-1 ring-black/[0.05] dark:ring-white/[0.06] p-6 sm:p-10 text-center shadow-[0_24px_48px_-24px_rgba(0,0,0,0.15)]">{t14}{t15}{t16}{t17}{t18}{t22}</div>;
    $[59] = t14;
    $[60] = t15;
    $[61] = t16;
    $[62] = t17;
    $[63] = t18;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  return t23;
}